import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { AdminPanelSettings as AdminIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const homePathForRole = (role: string) =>
    role === 'coach' || role === 'entreprise' ? '/tracking' : '/';

  // Redirect if already logged in
  React.useEffect(() => {
    if (user) {
      navigate(homePathForRole(user.role));
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      const stored = localStorage.getItem('admin_user');
      const loggedInUser = stored ? JSON.parse(stored) : null;
      navigate(homePathForRole(loggedInUser?.role || 'admin'));
    } catch (error: any) {
      const message =
        typeof error?.message === 'string' && error.message !== '[object Object]'
          ? error.message
          : 'Connexion impossible. Vérifiez vos identifiants puis réessayez.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            padding: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            width: '100%',
            bgcolor: '#281e30',
          }}
        >
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', width: '100%' }}>
            <img 
              src={`${process.env.PUBLIC_URL}/logo_fasterclass.svg`}
              alt="Fasterclass Logo" 
              style={{ height: '40px', width: 'auto', maxWidth: '200px' }}
              onError={(e) => {
                console.error('Erreur chargement logo');
                e.currentTarget.style.display = 'none';
              }}
            />
          </Box>
          
          <Typography component="h1" variant="h4" gutterBottom sx={{ color: '#fff' }} fontWeight="bold">
            Admin
          </Typography>
          <Typography variant="body1" sx={{ color: '#fff', opacity: 0.8 }} gutterBottom>
            Connectez-vous pour gérer les formations et les apprenants
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Adresse e-mail"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#49ffc1',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#49ffc1',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #281e30 inset',
                  WebkitTextFillColor: '#fff',
                },
                '& input:-webkit-autofill:hover': {
                  WebkitBoxShadow: '0 0 0 100px #281e30 inset',
                  WebkitTextFillColor: '#fff',
                },
                '& input:-webkit-autofill:focus': {
                  WebkitBoxShadow: '0 0 0 100px #281e30 inset',
                  WebkitTextFillColor: '#fff',
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Mot de passe"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#fff',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#49ffc1',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#49ffc1',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #281e30 inset',
                  WebkitTextFillColor: '#fff',
                },
                '& input:-webkit-autofill:hover': {
                  WebkitBoxShadow: '0 0 0 100px #281e30 inset',
                  WebkitTextFillColor: '#fff',
                },
                '& input:-webkit-autofill:focus': {
                  WebkitBoxShadow: '0 0 0 100px #281e30 inset',
                  WebkitTextFillColor: '#fff',
                },
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3, 
                mb: 1, 
                py: 1.5,
                bgcolor: '#49ffc1',
                color: '#281e30',
                fontWeight: 700,
                '&:hover': {
                  bgcolor: '#3de6ad',
                },
              }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#281e30' }} /> : 'Se connecter'}
            </Button>

            <Box textAlign="center" mb={1}>
              <Button
                onClick={() => navigate('/forgot-password')}
                sx={{ color: '#49ffc1', textTransform: 'none', fontWeight: 600 }}
              >
                Mot de passe oublié ?
              </Button>
            </Box>
            
            <Box textAlign="center">
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Accès réservé aux administrateurs, coachs et comptes entreprise
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 